import { refreshAccessToken } from '@/api/api.auth';
import { ApiError } from '@/types/errors';
import axios, { AxiosError, InternalAxiosRequestConfig, isAxiosError } from 'axios';
import secureLocalStorage from 'react-secure-storage';

import { callApiInstance, handleSuccess, maybeSetAuthHeaders } from './api.utils';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URI,
});

interface InternalAxiosRequestConfigWithRetry extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

api.interceptors.request.use(maybeSetAuthHeaders);
api.interceptors.response.use(handleSuccess, async (error: Error | AxiosError) => {
  if (!isAxiosError(error)) {
    return Promise.reject({ status: undefined, message: 'An error occurred.' });
  }

  const originalRequest: InternalAxiosRequestConfigWithRetry = error.config!;

  if (error.response?.status === 401 && !originalRequest._retry) {
    const refreshToken = secureLocalStorage.getItem('refresh_token') as string;
    if (refreshToken) {
      originalRequest._retry = true;
      try {
        await refreshAccessToken(refreshToken);
        const newToken = secureLocalStorage.getItem('token');
        if (newToken) {
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        return Promise.reject({
          status: 401,
          message: 'Unauthorized. Token refresh failed.',
        });
      }
    }
  }

  let errorMessage = 'An error occurred.';
  let errorCode: string | undefined;

  if (error.response?.data) {
    if (error.config?.responseType === 'blob') {
      // Manually parse the blob
      try {
        const text = await (error.response.data as Blob).text();
        const json = JSON.parse(text);
        errorMessage = json.detail || error.response.statusText || errorMessage;
        errorCode = json.code;
      } catch (parseError) {
        errorMessage = error.response.statusText || errorMessage;
      }
    } else {
      // Extract error information from response
      const responseData = error.response.data;
      errorMessage = responseData.detail || error.response.statusText || errorMessage;
      errorCode = responseData.code;
    }
  } else if (error.message) {
    // Network error or request setup error
    errorMessage = error.message;
    if (error.code === 'NETWORK_ERROR' || error.message.includes('Network Error')) {
      errorCode = 'network_error';
    } else if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      errorCode = 'timeout';
    }
  }

  const apiError: ApiError = {
    status: error.response?.status,
    message: errorMessage,
    code: errorCode,
  };

  return Promise.reject(apiError);
});

export const callApi = callApiInstance(api);
